import { organizationQueryKey } from '@cast/constants';
import { Organization, OrganizationType } from '@cast/types';

export const findOrganization = (
  organizationId?: string | null,
  organizations?: Organization[]
): Organization | undefined =>
  organizationId
    ? organizations?.find(({ id }) => id === organizationId)
    : undefined;

const findOrganizationFromStorage = (
  storageKey: string,
  organizations: Organization[],
  getStorageItem: (key: string) => string | null
): Organization | undefined => {
  const orgId = getStorageItem(storageKey);
  return orgId ? findOrganization(orgId, organizations) : undefined;
};

/**
 * Determines the current organization based on a priority hierarchy.
 *
 * Priority order:
 * 1. If enterprise context: finds child org matching query ID, or first child org, or fallback to default/child org
 * 2. Organization from query parameters
 * 3. Organization from session storage
 * 4. Organization from local storage
 * 5. First organization in the list
 *
 * @param organizations - Array of available organizations
 * @param orgIdFromQuery - Organization ID from URL query parameters
 * @param enterpriseOrgId - Enterprise organization ID for enterprise context
 * @returns The determined current organization or undefined if no organizations available
 *
 * @example
 * ```typescript
 * const currentOrg = determineCurrentOrganization(
 *   organizations,
 *   'org-from-url',
 *   'enterprise-123'
 * );
 * ```
 */
export const determineCurrentOrganization = (
  organizations?: Organization[],
  orgIdFromQuery?: string | null,
  enterpriseOrgId?: string
): Organization | undefined => {
  if (!organizations?.length) {
    return;
  }

  const orgFromQuery = findOrganization(orgIdFromQuery, organizations);
  if (enterpriseOrgId || orgFromQuery?.type === OrganizationType.ENTERPRISE) {
    const enterpriseOrg =
      findOrganization(enterpriseOrgId, organizations) || orgFromQuery;
    const childOrgs = organizations?.filter(
      (org) =>
        org.type === OrganizationType.CHILD &&
        org.parentId === (enterpriseOrgId ?? orgFromQuery?.id)
    );

    if (childOrgs?.length) {
      const childOrg =
        findOrganization(orgIdFromQuery, childOrgs) || childOrgs[0];

      return childOrg;
    }

    return enterpriseOrg;
  }

  if (orgFromQuery) {
    return orgFromQuery;
  }

  const orgFromSession = findOrganizationFromStorage(
    organizationQueryKey,
    organizations,
    (key) => sessionStorage.getItem(key)
  );
  if (orgFromSession) {
    return orgFromSession;
  }

  const orgFromLocalStorage = findOrganizationFromStorage(
    organizationQueryKey,
    organizations,
    (key) => localStorage.getItem(key)
  );
  if (orgFromLocalStorage) {
    return orgFromLocalStorage;
  }

  return organizations[0];
};
